// As consultas e mutações da tela de agentes. A lista de números entra na
// chave `canais` de propósito: é o evento de status do WhatsApp que a invalida.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../shared/api/api';
import { chaveDasConfiguracoes } from '../../shared/api/configuracoes';
import { useLoja } from '../../shared/estado/loja';
import { chaveDaMarca } from '../estudio/api';
import type { Agente, CatalogoDeTipo, DadosDoAgente, RespostaDoEmulador } from './tipos';

const loja = () => useLoja.getState().tenantId ?? '';
const base = () => `/api/tenant/${encodeURIComponent(loja())}`;

export const chaveDosAgentes = (tenantId: string) => ['agentes', tenantId] as const;

export function useAgentes() {
  const tenantId = useLoja((s) => s.tenantId) ?? '';
  return useQuery({
    queryKey: chaveDosAgentes(tenantId),
    queryFn: () => api.get<{ agents: Agente[] }>(`${base()}/agents`).then((r) => r.agents),
    enabled: tenantId !== '',
    // Sem plano: o 403 é a resposta, não uma falha passageira.
    retry: false,
  });
}

export function useTiposDeAgente() {
  const tenantId = useLoja((s) => s.tenantId) ?? '';
  return useQuery({
    queryKey: ['agentes', tenantId, 'tipos'],
    queryFn: () => api.get<{ ok: boolean; tipos: CatalogoDeTipo[] }>(`${base()}/agentes/tipos`).then((r) => r.tipos),
    enabled: tenantId !== '',
    staleTime: Infinity,
  });
}

/** Toda mutação recarrega a lista: é ela que diz o que está salvo. */
function useRecarregar() {
  const consultas = useQueryClient();
  const tenantId = useLoja((s) => s.tenantId) ?? '';
  return () => consultas.invalidateQueries({ queryKey: chaveDosAgentes(tenantId) });
}

/**
 * Nome, segmento e diferenciais são da loja: salvar um agente leva para os
 * outros, para a Marca e (o nome) para a Conta — o servidor grava, e elas releem.
 */
function useRecarregarComALoja() {
  const consultas = useQueryClient();
  const tenantId = useLoja((s) => s.tenantId) ?? '';
  return () => Promise.all([chaveDosAgentes(tenantId), chaveDaMarca(tenantId), chaveDasConfiguracoes(tenantId)]
    .map((queryKey) => consultas.invalidateQueries({ queryKey })));
}

export function useCriarAgente() {
  const recarregar = useRecarregarComALoja();
  return useMutation({
    mutationFn: (dados: { name: string; type: string }) => api.post<{ ok: boolean; agent: Agente }>(`${base()}/agents`, dados).then((r) => r.agent),
    onSuccess: () => recarregar(),
  });
}

export function useSalvarAgente() {
  const recarregar = useRecarregarComALoja();
  return useMutation({
    mutationFn: ({ id, dados }: { id: number; dados: DadosDoAgente }) => api.put<{ ok: boolean; agent: Agente }>(`${base()}/agents/${id}`, dados).then((r) => r.agent),
    onSuccess: () => recarregar(),
  });
}

export function usePausarAgente() {
  const recarregar = useRecarregar();
  return useMutation({
    mutationFn: ({ id, paused }: { id: number; paused: boolean }) => api.patch<{ ok: boolean; paused: boolean }>(`${base()}/agents/${id}/pause`, { paused }),
    onSuccess: () => recarregar(),
  });
}

export function useApagarAgente() {
  const recarregar = useRecarregar();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ ok: boolean }>(`${base()}/agents/${id}`),
    onSuccess: () => recarregar(),
  });
}

export interface MensagemDoEmulador { role: 'user' | 'assistant'; content: string; notes?: string[] }

/** `id` numérico roda o agente salvo; 'draft' roda o que está no formulário (`agent`). */
export function emular(id: number | 'draft', messages: MensagemDoEmulador[], agent?: Partial<DadosDoAgente>) {
  const corpo: { messages: { role: string; content: string }[]; agent?: Partial<DadosDoAgente> } = { messages: messages.map((m) => ({ role: m.role, content: m.content })) };
  if (agent) corpo.agent = agent;
  return api.post<RespostaDoEmulador>(`${base()}/agents/${id}/test`, corpo);
}
